import * as React from "react";
import type { SAValuation } from "@/data/sellerAnalysisTypes";
import { formatPriceFull } from "@/lib/format";
import { Reveal, Overline, CountUp } from "@/components/presentation/primitives";

interface Props {
  valuation: SAValuation;
}

const fmt = (n: number) => formatPriceFull(n) || "";

export const SA_MarketGlance: React.FC<Props> = ({ valuation }) => {
  const rangeLabel = valuation.rangeLabel || "Estimated market range";
  const midpointLabel = valuation.midpointLabel || "Midpoint";
  const askLabel = valuation.suggestedAskLabel || "Suggested launch";

  return (
    <section
      className="relative w-full overflow-hidden bg-charcoal py-24 md:py-32"
      data-testid="sa-market"
      aria-label="The market at a glance"
    >
      <div className="absolute inset-0 grain-overlay opacity-[0.07]" />
      <div className="relative mx-auto w-full max-w-4xl px-6 md:px-10">
        <Reveal>
          <Overline className="text-ivory/50">The number</Overline>
          <h2 className="mt-4 max-w-2xl font-serif text-3xl leading-tight text-ivory md:text-5xl">
            Where I would position the apartment today.
          </h2>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-14 border-y border-ivory/15 py-12">
            <p className="text-[0.7rem] uppercase tracking-[0.28em] text-brass">
              {rangeLabel}
            </p>
            <p className="mt-4 font-serif text-4xl leading-none text-ivory sm:text-5xl lg:text-6xl">
              <CountUp value={valuation.low} formatter={fmt} />
              <span className="mx-3 text-ivory/40">–</span>
              <CountUp value={valuation.high} formatter={fmt} />
            </p>
            {(valuation.ppsfLow != null || valuation.ppsfHigh != null) && (
              <p className="mt-4 text-sm uppercase tracking-[0.16em] text-ivory/55">
                ≈ ${valuation.ppsfLow?.toLocaleString("en-US")} – $
                {valuation.ppsfHigh?.toLocaleString("en-US")} / sqft
              </p>
            )}

            <div
              className={`mt-10 grid gap-8 ${
                valuation.suggestedAsk != null ? "sm:grid-cols-2" : ""
              }`}
            >
              {valuation.midpoint != null && (
                <div>
                  <p className="text-[0.7rem] uppercase tracking-[0.22em] text-ivory/50">
                    {midpointLabel}
                  </p>
                  <p className="mt-2 font-serif text-3xl text-ivory md:text-4xl">
                    ≈ {formatPriceFull(valuation.midpoint)}
                  </p>
                  {valuation.ppsfMid != null && (
                    <p className="mt-2 text-sm text-ivory/55">
                      ${valuation.ppsfMid.toLocaleString("en-US")} / sqft
                    </p>
                  )}
                </div>
              )}
              {valuation.suggestedAsk != null && (
                <div>
                  <p className="text-[0.7rem] uppercase tracking-[0.22em] text-brass">
                    {askLabel}
                  </p>
                  <p className="mt-2 font-serif text-3xl text-ivory md:text-4xl">
                    {formatPriceFull(valuation.suggestedAsk)}
                  </p>
                  {valuation.ppsfAsk != null && (
                    <p className="mt-2 text-sm text-ivory/55">
                      ${valuation.ppsfAsk.toLocaleString("en-US")} / sqft
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </Reveal>

        {(valuation.narrative || valuation.followUp) && (
          <Reveal delay={0.15}>
            <div className="mt-10 max-w-2xl space-y-4">
              {valuation.narrative && (
                <p className="text-base leading-relaxed text-ivory/80 md:text-lg">
                  {valuation.narrative}
                </p>
              )}
              {valuation.followUp && (
                <p className="font-serif text-xl italic text-ivory md:text-2xl">
                  {valuation.followUp}
                </p>
              )}
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
};
